import type { MapSnapshot, ScreenPoint } from './map';
import { timelinePeriod, type TimelineAnalysis } from './timeline';
import { videoMapSafeFrame, videoOverlayLayout } from './video-layout';

export interface StoryChapter {
  readonly kicker: string;
  readonly headline: string;
  readonly detail: string;
}

export interface PreparedVideo {
  readonly snapshot: MapSnapshot;
  readonly brandIcon?: CanvasImageSource;
  readonly cumulativeDistance: number[];
  readonly totalDistance: number;
  readonly title: string;
  readonly period: string;
  readonly durationSeconds: number;
  readonly chapters: StoryChapter[];
  readonly cameraSegments: CameraSegment[];
  readonly analysis: TimelineAnalysis;
}

export interface CameraSegment {
  readonly startIndex: number;
  readonly endIndex: number;
}

export interface StoryPlaybackStatus {
  readonly date: string;
  readonly distanceKm: number;
}

export const VIDEO_WATERMARK_TEXT = '오늘툴';
export const VIDEO_BRAND_NAME = '오늘툴.';
export const VIDEO_BRAND_URL = 'oneultool.com';
export const MAX_VISIBLE_COUNTRIES = 5;
export const MAX_CAMERA_TRANSITIONS = 4;
export const ROUTE_TRAIL_STYLE = {
  olderOpacity: 0.32,
  olderWidth: 2.8,
  recentRetention: 0.24,
  recentOpacity: 0.62,
  recentWidth: 4.1,
  freshRetention: 0.1,
  freshOpacity: 0.96,
  freshWidth: 4.8,
} as const;

export interface CameraState {
  readonly centerX: number;
  readonly centerY: number;
  readonly zoom: number;
  readonly routeProgress: number;
}

function distance(a: ScreenPoint, b: ScreenPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function roundedKilometers(value: number): string {
  if (value <= 0) return '0';
  if (value < 10) return value.toFixed(1);
  return Math.round(value).toLocaleString('ko-KR');
}

export function storyVideoTitle(durationSeconds: number, customTitle = ''): string {
  return customTitle.trim() || `내 역마살 ${durationSeconds}초`;
}

export function buildStoryChapters(analysis: TimelineAnalysis): StoryChapter[] {
  const chapters: StoryChapter[] = [{
    kicker: '이번 기간 이동 거리',
    headline: `약 ${roundedKilometers(analysis.totalDistanceKm)} km`,
    detail: `${analysis.recordedDayCount}일 동안 타임라인에 남은 기록`,
  }];

  if (analysis.countries.length > 1) {
    const countryNames = analysis.countries
      .slice(0, MAX_VISIBLE_COUNTRIES)
      .map((country) => `${country.flag} ${country.name}`)
      .join(' · ');
    chapters.push({
      kicker: '타임라인에 남은 여행지',
      headline: countryNames,
      detail: analysis.countries.length > MAX_VISIBLE_COUNTRIES
        ? `그 밖에 ${analysis.countries.length - MAX_VISIBLE_COUNTRIES}개 나라가 더 있어요`
        : `${analysis.countries.length}개 나라의 이동 기록`,
    });
  }
  return chapters;
}

function buildCameraSegments(
  route: readonly ScreenPoint[],
  countryCount: number,
  cumulativeDistance: readonly number[],
): CameraSegment[] {
  const lastIndex = Math.max(0, route.length - 1);
  if (countryCount <= 1 || route.length < 3) {
    return [{ startIndex: 0, endIndex: lastIndex }];
  }

  const xs = route.map((point) => point.x);
  const ys = route.map((point) => point.y);
  const routeDiagonal = Math.hypot(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...ys) - Math.min(...ys),
  );
  const largeMoveThreshold = Math.max(72, routeDiagonal * 0.18);
  const totalDistance = cumulativeDistance.at(-1) ?? 0;
  const largeMoves = route.slice(1).map((point, offset) => ({
    index: offset + 1,
    distance: distance(route[offset], point),
    progress: totalDistance > 0
      ? ((cumulativeDistance[offset] ?? 0) + (cumulativeDistance[offset + 1] ?? 0)) / 2 / totalDistance
      : 1,
  })).filter((move) => move.distance >= largeMoveThreshold)
    .sort((a, b) => b.distance - a.distance);
  const selectedMoves: typeof largeMoves = [];
  for (const move of largeMoves) {
    if (selectedMoves.every((selected) => Math.abs(selected.progress - move.progress) >= 0.18)) {
      selectedMoves.push(move);
    }
    if (selectedMoves.length >= MAX_CAMERA_TRANSITIONS) break;
  }
  const splitIndexes = selectedMoves
    .map((move) => move.index)
    .sort((a, b) => a - b);

  if (splitIndexes.length === 0) return [{ startIndex: 0, endIndex: lastIndex }];
  const segments: CameraSegment[] = [];
  let startIndex = 0;
  for (const splitIndex of splitIndexes) {
    segments.push({ startIndex, endIndex: Math.max(startIndex, splitIndex - 1) });
    startIndex = splitIndex;
  }
  segments.push({ startIndex, endIndex: lastIndex });
  return segments;
}

export function resultScreenSeconds(durationSeconds: number): number {
  return clamp(durationSeconds * 0.12, 2.4, 3);
}

export function prepareVideo(
  snapshot: MapSnapshot,
  analysis: TimelineAnalysis,
  durationSeconds = 20,
  customTitle = '',
): PreparedVideo {
  const cumulativeDistance = new Array<number>(snapshot.route.length).fill(0);
  for (let index = 1; index < snapshot.route.length; index += 1) {
    // A single overseas jump must not consume most of the 20-second video.
    // Capping each leg leaves meaningful playback time for dense local paths.
    const playbackWeight = clamp(distance(snapshot.route[index - 1], snapshot.route[index]), 0.25, 24);
    cumulativeDistance[index] = cumulativeDistance[index - 1]
      + playbackWeight;
  }
  return {
    snapshot,
    cumulativeDistance,
    totalDistance: cumulativeDistance.at(-1) ?? 0,
    title: storyVideoTitle(durationSeconds, customTitle),
    period: timelinePeriod(analysis.points),
    durationSeconds,
    chapters: buildStoryChapters(analysis),
    cameraSegments: buildCameraSegments(snapshot.route, analysis.countries.length, cumulativeDistance),
    analysis,
  };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function mix(start: number, end: number, progress: number): number {
  return start + (end - start) * progress;
}

function smoothStep(value: number): number {
  const bounded = clamp(value, 0, 1);
  return bounded * bounded * (3 - 2 * bounded);
}

function routePosition(
  video: PreparedVideo,
  rawProgress: number,
): { index: number; point: ScreenPoint; fraction: number; playbackDistance: number } {
  const progress = clamp(rawProgress, 0, 1);
  const target = video.totalDistance * progress;
  let low = 0;
  let high = video.cumulativeDistance.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (video.cumulativeDistance[middle] < target) low = middle + 1;
    else high = middle;
  }
  const index = Math.max(1, low);
  const beforeDistance = video.cumulativeDistance[index - 1];
  const segmentDistance = Math.max(0.0001, video.cumulativeDistance[index] - beforeDistance);
  const fraction = clamp((target - beforeDistance) / segmentDistance, 0, 1);
  const before = video.snapshot.route[index - 1];
  const after = video.snapshot.route[index];
  return {
    index,
    fraction,
    playbackDistance: target,
    point: {
      x: mix(before.x, after.x, fraction),
      y: mix(before.y, after.y, fraction),
    },
  };
}

export function storyPlaybackStatus(
  video: PreparedVideo,
  routeProgress: number,
): StoryPlaybackStatus {
  const current = routePosition(video, routeProgress);
  const pointCount = video.analysis.points.length;
  const beforeIndex = clamp(current.index - 1, 0, Math.max(0, pointCount - 1));
  const afterIndex = clamp(current.index, 0, Math.max(0, pointCount - 1));
  const before = video.analysis.points[beforeIndex]?.timestamp.getTime() ?? 0;
  const after = video.analysis.points[afterIndex]?.timestamp.getTime() ?? before;
  const timestamp = mix(before, after, current.fraction);
  const date = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(timestamp));
  return {
    date,
    distanceKm: video.analysis.totalDistanceKm * clamp(routeProgress, 0, 1),
  };
}

interface CameraTarget {
  readonly centerX: number;
  readonly centerY: number;
  readonly zoom: number;
}

function routeSegmentCamera(
  video: PreparedVideo,
  segment: CameraSegment,
  width: number,
  height: number,
): CameraTarget {
  const points = video.snapshot.route.slice(segment.startIndex, segment.endIndex + 1);
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minimumX = Math.min(...xs);
  const maximumX = Math.max(...xs);
  const minimumY = Math.min(...ys);
  const maximumY = Math.max(...ys);
  const spanX = Math.max(1, maximumX - minimumX);
  const spanY = Math.max(1, maximumY - minimumY);
  const safeFrame = videoMapSafeFrame(width, height);
  const zoom = clamp(Math.min(
    (safeFrame.right - safeFrame.left) / spanX,
    (safeFrame.bottom - safeFrame.top) / spanY,
  ), 1, 2.05);
  const targetCenterY = (safeFrame.top + safeFrame.bottom) / 2;
  return {
    centerX: (minimumX + maximumX) / 2,
    centerY: (minimumY + maximumY) / 2 + (height / 2 - targetCenterY) / zoom,
    zoom,
  };
}

function mixCamera(start: CameraTarget, end: CameraTarget, progress: number): CameraTarget {
  return {
    centerX: mix(start.centerX, end.centerX, progress),
    centerY: mix(start.centerY, end.centerY, progress),
    zoom: mix(start.zoom, end.zoom, progress),
  };
}

function routeOverviewCenter(video: PreparedVideo): ScreenPoint {
  const xs = video.snapshot.route.map((point) => point.x);
  const ys = video.snapshot.route.map((point) => point.y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

function overviewCamera(video: PreparedVideo, width: number, height: number): CameraTarget {
  const center = routeOverviewCenter(video);
  const safeFrame = videoMapSafeFrame(width, height);
  const targetCenterY = (safeFrame.top + safeFrame.bottom) / 2;
  return {
    centerX: center.x,
    centerY: center.y + height / 2 - targetCenterY,
    zoom: 1,
  };
}

function regionalCameraForProgress(
  video: PreparedVideo,
  routeProgress: number,
  width: number,
  height: number,
): CameraTarget {
  const targets = video.cameraSegments.map((segment) => (
    routeSegmentCamera(video, segment, width, height)
  ));
  let camera = targets[0];
  const transitionSpan = Math.min(
    0.18,
    (2.6 / video.durationSeconds) / Math.max(0.01, 1 - resultScreenSeconds(video.durationSeconds) / video.durationSeconds),
  );

  for (let index = 1; index < video.cameraSegments.length; index += 1) {
    const splitIndex = video.cameraSegments[index].startIndex;
    const before = video.cumulativeDistance[Math.max(0, splitIndex - 1)] ?? 0;
    const after = video.cumulativeDistance[splitIndex] ?? before;
    const boundary = video.totalDistance > 0
      ? ((before + after) / 2) / video.totalDistance
      : 1;
    const transitionStart = boundary - transitionSpan / 2;
    const transitionEnd = boundary + transitionSpan / 2;
    if (routeProgress < transitionStart) break;
    if (routeProgress <= transitionEnd) {
      return mixCamera(
        targets[index - 1],
        targets[index],
        smoothStep((routeProgress - transitionStart) / transitionSpan),
      );
    }
    camera = targets[index];
  }
  return camera;
}

export function cameraForProgress(
  video: PreparedVideo,
  progress: number,
  width: number,
  height: number,
): CameraState {
  const boundedProgress = clamp(progress, 0, 1);
  const resultStart = 1 - resultScreenSeconds(video.durationSeconds) / video.durationSeconds;
  const routeProgress = clamp(boundedProgress / resultStart, 0, 1);
  const overview = overviewCamera(video, width, height);
  let camera = video.cameraSegments.length === 1
    ? overview
    : regionalCameraForProgress(video, routeProgress, width, height);
  const overviewTransitionDuration = Math.min(2 / video.durationSeconds, resultStart * 0.2);
  const overviewTransitionStart = resultStart - overviewTransitionDuration;
  if (boundedProgress > overviewTransitionStart) {
    const returnFrom = video.cameraSegments.length === 1
      ? overview
      : regionalCameraForProgress(
        video,
        overviewTransitionStart / resultStart,
        width,
        height,
      );
    camera = mixCamera(
      returnFrom,
      overview,
      smoothStep((boundedProgress - overviewTransitionStart) / overviewTransitionDuration),
    );
  }

  const halfWidth = width / (2 * camera.zoom);
  const halfHeight = height / (2 * camera.zoom);
  return {
    centerX: clamp(camera.centerX, halfWidth, width - halfWidth),
    centerY: clamp(camera.centerY, halfHeight, height - halfHeight),
    zoom: camera.zoom,
    routeProgress,
  };
}

function strokeRouteRange(
  context: CanvasRenderingContext2D,
  points: readonly ScreenPoint[],
  startIndex = 0,
  endIndex = points.length,
  head?: ScreenPoint,
): void {
  if (points.length === 0) return;
  const boundedStart = clamp(startIndex, 0, Math.max(0, endIndex - 1));
  context.beginPath();
  context.moveTo(points[boundedStart].x, points[boundedStart].y);
  for (let index = boundedStart + 1; index < endIndex; index += 1) {
    context.lineTo(points[index].x, points[index].y);
  }
  if (head) context.lineTo(head.x, head.y);
  context.stroke();
}

function trailStartIndex(
  video: PreparedVideo,
  current: ReturnType<typeof routePosition>,
  retention: number,
  minimumLength: number,
): number {
  const trailLength = Math.max(video.totalDistance * retention, minimumLength);
  const target = Math.max(0, current.playbackDistance - trailLength);
  let low = 0;
  let high = current.index;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (video.cumulativeDistance[middle] < target) low = middle + 1;
    else high = middle;
  }
  return Math.min(Math.max(0, current.index - 1), low);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fill();
}

function fitHeadline(
  context: CanvasRenderingContext2D,
  text: string,
  maximumWidth: number,
  preferredSize: number,
  minimumSize: number,
): number {
  let size = preferredSize;
  while (size > minimumSize) {
    context.font = `700 ${size}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    if (context.measureText(text).width <= maximumWidth) return size;
    size -= 1;
  }
  return size;
}

export function drawVideoFrame(
  canvas: HTMLCanvasElement,
  video: PreparedVideo,
  progress: number,
): void {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('브라우저에서 영상 프레임을 그릴 수 없습니다.');
  const width = canvas.width;
  const height = canvas.height;
  const scale = Math.min(width, height) / 720;
  const overlayLayout = videoOverlayLayout(width, height);
  const camera = cameraForProgress(video, progress, width, height);
  const current = routePosition(video, camera.routeProgress);
  const playbackStatus = storyPlaybackStatus(video, camera.routeProgress);
  const resultStart = 1 - resultScreenSeconds(video.durationSeconds) / video.durationSeconds;
  const resultPhaseProgress = clamp((progress - resultStart) / (1 - resultStart), 0, 1);
  const resultOpacity = smoothStep(resultPhaseProgress / 0.18);

  context.clearRect(0, 0, width, height);
  context.save();
  context.translate(width / 2, height / 2);
  context.scale(camera.zoom, camera.zoom);
  context.translate(-camera.centerX, -camera.centerY);
  context.drawImage(video.snapshot.background, 0, 0, width, height);
  context.fillStyle = 'rgba(255,255,255,.14)';
  context.fillRect(0, 0, width, height);

  context.lineCap = 'round';
  context.lineJoin = 'round';

  if (current.index > 0) {
    context.strokeStyle = `rgba(95, 51, 225, ${ROUTE_TRAIL_STYLE.olderOpacity})`;
    context.lineWidth = ROUTE_TRAIL_STYLE.olderWidth * scale / camera.zoom;
    strokeRouteRange(context, video.snapshot.route, 0, current.index, current.point);
  }

  const recentTrailStart = trailStartIndex(
    video,
    current,
    ROUTE_TRAIL_STYLE.recentRetention,
    36,
  );
  context.strokeStyle = 'rgba(255, 255, 255, .68)';
  context.lineWidth = 6.2 * scale / camera.zoom;
  strokeRouteRange(context, video.snapshot.route, recentTrailStart, current.index, current.point);
  context.strokeStyle = `rgba(95, 51, 225, ${ROUTE_TRAIL_STYLE.recentOpacity})`;
  context.lineWidth = ROUTE_TRAIL_STYLE.recentWidth * scale / camera.zoom;
  strokeRouteRange(context, video.snapshot.route, recentTrailStart, current.index, current.point);

  const freshTrailStart = trailStartIndex(
    video,
    current,
    ROUTE_TRAIL_STYLE.freshRetention,
    16,
  );
  context.strokeStyle = 'rgba(255, 255, 255, .88)';
  context.lineWidth = 7 * scale / camera.zoom;
  strokeRouteRange(context, video.snapshot.route, freshTrailStart, current.index, current.point);
  context.strokeStyle = `rgba(95, 39, 255, ${ROUTE_TRAIL_STYLE.freshOpacity})`;
  context.lineWidth = ROUTE_TRAIL_STYLE.freshWidth * scale / camera.zoom;
  strokeRouteRange(context, video.snapshot.route, freshTrailStart, current.index, current.point);

  const pulse = 1 + Math.sin(progress * Math.PI * 28) * 0.08;
  context.fillStyle = 'rgba(95, 51, 225, .24)';
  context.beginPath();
  context.arc(current.point.x, current.point.y, 15 * scale * pulse / camera.zoom, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = '#FFFFFF';
  context.beginPath();
  context.arc(current.point.x, current.point.y, 9 * scale / camera.zoom, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = '#5F33E1';
  context.beginPath();
  context.arc(current.point.x, current.point.y, 5 * scale / camera.zoom, 0, Math.PI * 2);
  context.fill();
  context.restore();

  const side = 36 * scale;
  const title = overlayLayout.title;
  context.save();
  context.fillStyle = 'rgba(255,255,255,.91)';
  roundedRect(context, title.left, title.top, title.width, title.height, 18 * scale);
  context.textAlign = 'center';
  context.fillStyle = '#24252C';
  const titleSize = fitHeadline(
    context,
    video.title,
    title.width - 40 * scale,
    23 * scale,
    15 * scale,
  );
  context.font = `700 ${titleSize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
  context.fillText(video.title, width / 2, title.top + 38 * scale);
  context.fillStyle = '#6E6A7C';
  context.font = `600 ${16 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
  context.fillText(
    `${playbackStatus.date} · 누적 ${roundedKilometers(playbackStatus.distanceKm)} km`,
    width / 2,
    title.top + 72 * scale,
  );
  context.restore();

  if (resultOpacity > 0) {
    const information = overlayLayout.information;
    const distanceChapter = video.chapters[0];
    const countryChapter = video.chapters[1];
    context.save();
    context.globalAlpha = resultOpacity;
    context.fillStyle = 'rgba(255,255,255,.95)';
    context.shadowColor = 'rgba(36,37,44,.12)';
    context.shadowBlur = 22 * scale;
    roundedRect(
      context,
      information.left,
      information.top,
      information.width,
      information.height,
      18 * scale,
    );
    context.shadowBlur = 0;
    context.textAlign = 'center';
    context.fillStyle = '#5F33E1';
    context.font = `700 ${12 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    context.fillText('전체 기간', width / 2, information.top + information.height * 0.12);
    context.fillStyle = '#24252C';
    const periodSize = fitHeadline(
      context,
      video.period,
      information.width - 40 * scale,
      20 * scale,
      13 * scale,
    );
    context.font = `700 ${periodSize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    context.fillText(video.period, width / 2, information.top + information.height * 0.27);
    context.fillStyle = '#6E6A7C';
    context.font = `600 ${11 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    context.fillText('총 이동 거리', width / 2, information.top + information.height * 0.42);
    context.fillStyle = '#24252C';
    context.font = `700 ${25 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    context.fillText(distanceChapter.headline, width / 2, information.top + information.height * 0.57);

    if (countryChapter) {
      context.fillStyle = '#5F33E1';
      context.font = `700 ${11 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
      context.fillText(countryChapter.kicker, width / 2, information.top + information.height * 0.72);
      context.fillStyle = '#24252C';
      const countrySize = fitHeadline(
        context,
        countryChapter.headline,
        information.width - 36 * scale,
        17 * scale,
        10 * scale,
      );
      context.font = `700 ${countrySize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
      context.fillText(countryChapter.headline, width / 2, information.top + information.height * 0.86);
      context.fillStyle = '#6E6A7C';
      context.font = `500 ${10 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
      context.fillText(countryChapter.detail, width / 2, information.top + information.height * 0.95);
    } else {
      context.fillStyle = '#6E6A7C';
      context.font = `500 ${11 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
      context.fillText(
        distanceChapter.detail,
        width / 2,
        information.top + information.height * 0.8,
      );
    }

    const brand = overlayLayout.brand;
    const story = height / width >= 1.45;
    context.fillStyle = 'rgba(255,255,255,.95)';
    context.shadowColor = 'rgba(36,37,44,.1)';
    context.shadowBlur = 16 * scale;
    roundedRect(
      context,
      brand.left,
      brand.top,
      brand.width,
      brand.height,
      16 * scale,
    );
    context.shadowBlur = 0;

    const iconSize = (story ? 42 : 36) * scale;
    const contentGap = 12 * scale;
    const brandNameSize = (story ? 17 : 16) * scale;
    const brandUrlSize = (story ? 11 : 10) * scale;
    context.font = `700 ${brandNameSize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    const brandNameWidth = context.measureText(VIDEO_BRAND_NAME).width;
    context.font = `600 ${brandUrlSize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    const brandUrlWidth = context.measureText(VIDEO_BRAND_URL).width;
    const textWidth = Math.max(brandNameWidth, brandUrlWidth);
    const contentWidth = iconSize + contentGap + textWidth;
    const iconLeft = brand.left + (brand.width - contentWidth) / 2;
    const iconTop = brand.top + (brand.height - iconSize) / 2;

    if (video.brandIcon) {
      context.drawImage(video.brandIcon, iconLeft, iconTop, iconSize, iconSize);
    } else {
      context.fillStyle = '#5F33E1';
      roundedRect(context, iconLeft, iconTop, iconSize, iconSize, iconSize * 0.225);
      context.fillStyle = '#FFFFFF';
      context.font = `700 ${iconSize * 0.47}px "Noto Sans KR", sans-serif`;
      context.textAlign = 'center';
      context.fillText('오.', iconLeft + iconSize / 2, iconTop + iconSize * 0.68);
    }

    const textLeft = iconLeft + iconSize + contentGap;
    context.textAlign = 'left';
    context.fillStyle = '#24252C';
    context.font = `700 ${brandNameSize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    context.fillText(VIDEO_BRAND_NAME, textLeft, brand.top + (story ? 29 : 26) * scale);
    context.fillStyle = '#5F33E1';
    context.font = `600 ${brandUrlSize}px "Lexend Deca", "Noto Sans KR", sans-serif`;
    context.fillText(VIDEO_BRAND_URL, textLeft, brand.top + (story ? 52 : 46) * scale);
    context.restore();
  }

  const attribution = 'OpenFreeMap · © OpenMapTiles · Data © OpenStreetMap contributors';
  const attributionHeight = 32 * scale;
  context.font = `500 ${9 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
  const attributionWidth = Math.min(width - side * 2, context.measureText(attribution).width + 22 * scale);
  context.fillStyle = 'rgba(255,255,255,.86)';
  roundedRect(context, side, height - side - attributionHeight, attributionWidth, attributionHeight, 8 * scale);
  context.fillStyle = '#4C4957';
  context.fillText(attribution, side + 11 * scale, height - side - 11 * scale);

  context.save();
  context.font = `700 ${13 * scale}px "Lexend Deca", "Noto Sans KR", sans-serif`;
  context.textAlign = 'right';
  context.lineWidth = 3 * scale;
  context.strokeStyle = 'rgba(255,255,255,.72)';
  context.strokeText(VIDEO_WATERMARK_TEXT, width - side, height - side - 10 * scale);
  context.fillStyle = 'rgba(36,37,44,.62)';
  context.fillText(VIDEO_WATERMARK_TEXT, width - side, height - side - 10 * scale);
  context.restore();
}
